/**
 * Role code -> human display name (BOOT-02).
 *
 * The host app passes a role CODE on the boot URL (`?Role=RA`), which is
 * what we write to the Sheet and what the backend shards on. `RA` is not a
 * thing to show a frontliner, so this is the lookup that turns it into
 * "Retail Assistant".
 *
 * The table is sourced from the client's own role registry export, not invented
 * here, and lives in JSON so adding a role is an authoring change rather than a
 * code change -- the same principle as the training content.
 *
 * NOT to be confused with two other role vocabularies already in the repo:
 *   - `src/content/roles.json` maps a role to its CURRICULUM. Different concern,
 *     and its one entry (`cashier`) is not a code in this table -- a gap worth
 *     closing when the curriculum manifest is filled in for real.
 *   - `StaffRole` in `src/screens/ordering/types.ts` is the ordering
 *     simulation's own demo vocabulary (`team-lead`, `storekeeper`, ...). It is
 *     unrelated, and merging the two would let a lesson demo link rewrite who
 *     the learner is.
 */
import catalogue from '@/content/role-labels.json'

export interface RoleLabel {
  readonly code: string
  readonly displayName: string
  readonly description: string
  readonly isActive: boolean
  /** Reserved by the client's registry; never assigned to a real job code. */
  readonly isSystem: boolean
}

export const ROLE_LABELS: readonly RoleLabel[] = catalogue.roles

const BY_CODE: ReadonlyMap<string, RoleLabel> = new Map(
  ROLE_LABELS.map((role) => [role.code.toUpperCase(), role]),
)

/**
 * Lookup is trimmed and case-folded because the host is a black box and
 * `PITFALLS` Pitfall 12 records that it has sent differing case per platform.
 */
function normalise(code: string): string {
  return code.trim().toUpperCase()
}

/** The full entry for a code, or `null` when the registry does not know it. */
export function findRole(code: string): RoleLabel | null {
  return BY_CODE.get(normalise(code)) ?? null
}

/**
 * Display name for a code.
 *
 * An unknown code returns the code as the host sent it (trimmed only), NOT a
 * guess and NOT a default role: `docs/TECH-SPEC.md:158` requires an unrecognised
 * role to surface its raw value so support can see what actually arrived.
 * Returns '' for a blank code so callers keep their own "no role" wording.
 */
export function roleDisplayName(code: string): string {
  const trimmed = code.trim()
  if (trimmed === '') return ''
  return findRole(trimmed)?.displayName ?? trimmed
}

/** True when the registry knows this code. Callers decide what to do about it. */
export function isKnownRole(code: string): boolean {
  return findRole(code) !== null
}
